import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import type { ReactNode } from 'react';

import { RegisterProviderModal } from './register-provider-modal';
import { useNewDispatchPlacePage } from './use-new-dispatch-place-page';

// ── Helpers ──────────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  error?: string;
  children: ReactNode;
}

function Field(props: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium">{props.label}</label>
      {props.children}
      {props.error && <p className="text-xs text-error">{props.error}</p>}
    </div>
  );
}

interface SectionProps {
  title: string;
  children: ReactNode;
}

function FormSection(props: SectionProps) {
  return (
    <div className="card border border-base-300 bg-base-100">
      <div className="card-body gap-4 p-5">
        <h2 className="text-base font-semibold text-base-content/80">{props.title}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{props.children}</div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Form to create a new dispatch place.
 *
 * ⚠️  Backend note: POST /admin/dispatch_places does not yet exist in the
 * rewrite (dispatch_place_controller.ex). The service, hook and form are
 * production-ready. Wire up the controller action when the endpoint is built.
 *
 * ⚠️  Signed URL note: GET /admin/providers/signed_url is also pending.
 * Contract upload is wired but will 404 until the endpoint is implemented.
 */
export function NewDispatchPlacePage() {
  const {
    form,
    is_submitting,
    is_register_modal_open,
    provider_search,
    filtered_providers,
    onSelectProvider,
    onRegisterProvider,
    onOpenRegisterModal,
    onCloseRegisterModal,
    onProviderSearchChange,
    onSubmit,
  } = useNewDispatchPlacePage();

  const { register, formState, watch } = form;
  const errors = formState.errors;
  const selected_provider_name = watch('provider_name');

  return (
    <div className="flex flex-col gap-6 pb-10">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/dispatch-places" className="btn btn-ghost btn-sm btn-circle">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Añadir Punto99</h1>
          <p className="text-base-content/60 text-sm">
            Completa todos los campos para registrar el nuevo punto de despacho.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">

        {/* ── Generales ─────────────────────────────────────────────────── */}
        <FormSection title="Información general">
          <Field label="Nombre del punto" error={errors.name?.message}>
            <input
              {...register('name')}
              className={twMerge('input input-bordered w-full', errors.name && 'input-error')}
              placeholder="Ej. Masaryk Polanco"
            />
          </Field>

          <Field label="Tipo" error={errors.type?.message}>
            <select {...register('type')} className="select select-bordered w-full">
              <option value="physical">Físico</option>
              <option value="locker">Locker</option>
              <option value="virtual">Virtual</option>
            </select>
          </Field>

          <Field label="País" error={errors.country_code?.message}>
            <select {...register('country_code')} className="select select-bordered w-full">
              <option value="MEX">México</option>
              <option value="COL">Colombia</option>
              <option value="CHL">Chile</option>
              <option value="PER">Perú</option>
            </select>
          </Field>

          <Field label="Estado inicial" error={errors.status?.message}>
            <select {...register('status')} className="select select-bordered w-full">
              <option value="pending">Pendiente</option>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </Field>

          <Field label="Capacidad (paquetes)" error={errors.capacity?.message}>
            <input
              {...register('capacity')}
              type="number"
              min={0}
              className={twMerge('input input-bordered w-full', errors.capacity && 'input-error')}
              placeholder="0"
            />
          </Field>

          <Field label="Descripción" error={errors.description?.message}>
            <textarea
              {...register('description')}
              className={twMerge('textarea textarea-bordered w-full', errors.description && 'textarea-error')}
              rows={2}
              placeholder="Descripción breve del punto"
            />
          </Field>
        </FormSection>

        {/* ── Proveedor ─────────────────────────────────────────────────── */}
        <div className="card border border-base-300 bg-base-100">
          <div className="card-body gap-4 p-5">
            <h2 className="text-base font-semibold text-base-content/80">Proveedor</h2>

            <div className="flex flex-col gap-3">
              {/* Selected provider badge */}
              {selected_provider_name && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-base-content/60">Seleccionado:</span>
                  <span className="badge badge-primary badge-outline">{selected_provider_name}</span>
                </div>
              )}

              {/* Search input */}
              <Field label="Buscar proveedor" error={errors.provider_id?.message}>
                <div className="relative">
                  <input
                    type="text"
                    className={twMerge(
                      'input input-bordered w-full',
                      errors.provider_id && 'input-error',
                    )}
                    placeholder="Escribe para buscar..."
                    value={provider_search}
                    onChange={(e) => onProviderSearchChange(e.target.value)}
                  />
                  {/* Dropdown */}
                  {provider_search.length > 0 &&
                    filtered_providers.length > 0 &&
                    !filtered_providers.some((p) => p.name === selected_provider_name) && (
                      <ul className="absolute z-10 mt-1 w-full rounded-box border border-base-300 bg-base-100 shadow-lg">
                        {filtered_providers.map((provider) => (
                          <li key={provider.id}>
                            <button
                              type="button"
                              className="w-full px-4 py-2 text-left text-sm hover:bg-base-200 transition-colors"
                              onClick={() => onSelectProvider(provider.id, provider.name)}
                            >
                              {provider.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              </Field>

              {/* New provider CTA */}
              {provider_search.length > 0 && filtered_providers.length === 0 && (
                <div className="flex items-center gap-3 rounded-box border border-base-300 bg-base-200/50 px-4 py-3">
                  <span className="text-sm text-base-content/60 flex-1">
                    No se encontró "{provider_search}"
                  </span>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm gap-1"
                    onClick={onOpenRegisterModal}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Dar de alta proveedor
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Ubicación ─────────────────────────────────────────────────── */}
        <FormSection title="Ubicación (coordenadas)">
          <Field label="Latitud" error={errors.latitude?.message}>
            <input
              {...register('latitude')}
              type="number"
              step="any"
              className={twMerge('input input-bordered w-full', errors.latitude && 'input-error')}
              placeholder="19.4326"
            />
          </Field>

          <Field label="Longitud" error={errors.longitude?.message}>
            <input
              {...register('longitude')}
              type="number"
              step="any"
              className={twMerge('input input-bordered w-full', errors.longitude && 'input-error')}
              placeholder="-99.1332"
            />
          </Field>
        </FormSection>

        {/* ── Dirección ─────────────────────────────────────────────────── */}
        <FormSection title="Dirección">
          <Field label="Calle" error={errors.address_street?.message}>
            <input
              {...register('address_street')}
              className={twMerge('input input-bordered w-full', errors.address_street && 'input-error')}
              placeholder="Av. Presidente Masaryk"
            />
          </Field>

          <Field label="Número" error={errors.address_number?.message}>
            <input
              {...register('address_number')}
              className={twMerge('input input-bordered w-full', errors.address_number && 'input-error')}
              placeholder="360"
            />
          </Field>

          <Field label="Colonia" error={errors.address_neighborhood?.message}>
            <input
              {...register('address_neighborhood')}
              className={twMerge('input input-bordered w-full', errors.address_neighborhood && 'input-error')}
              placeholder="Polanco"
            />
          </Field>

          <Field label="Ciudad" error={errors.address_city?.message}>
            <input
              {...register('address_city')}
              className={twMerge('input input-bordered w-full', errors.address_city && 'input-error')}
              placeholder="Ciudad de México"
            />
          </Field>

          <Field label="Estado" error={errors.address_state?.message}>
            <input
              {...register('address_state')}
              className={twMerge('input input-bordered w-full', errors.address_state && 'input-error')}
              placeholder="CDMX"
            />
          </Field>

          <Field label="Código postal" error={errors.address_postal_code?.message}>
            <input
              {...register('address_postal_code')}
              className={twMerge('input input-bordered w-full', errors.address_postal_code && 'input-error')}
              placeholder="11560"
            />
          </Field>

          <Field label="País (dirección)" error={errors.address_country?.message}>
            <input
              {...register('address_country')}
              className={twMerge('input input-bordered w-full', errors.address_country && 'input-error')}
              placeholder="México"
            />
          </Field>
        </FormSection>

        {/* ── Contacto ──────────────────────────────────────────────────── */}
        <FormSection title="Contacto">
          <Field label="Nombre" error={errors.contact_name?.message}>
            <input
              {...register('contact_name')}
              className={twMerge('input input-bordered w-full', errors.contact_name && 'input-error')}
              placeholder="Carlos Ramírez"
            />
          </Field>

          <Field label="Teléfono" error={errors.contact_phone?.message}>
            <input
              {...register('contact_phone')}
              type="tel"
              className={twMerge('input input-bordered w-full', errors.contact_phone && 'input-error')}
              placeholder="+52 55 1234 5678"
            />
          </Field>

          <Field label="Email" error={errors.contact_email?.message}>
            <input
              {...register('contact_email')}
              type="email"
              className={twMerge('input input-bordered w-full', errors.contact_email && 'input-error')}
              placeholder="contacto@punto99.mx"
            />
          </Field>
        </FormSection>

        {/* ── Facturación ───────────────────────────────────────────────── */}
        <FormSection title="Facturación">
          <Field label="Tipo de ID fiscal" error={errors.billing_tax_id_type?.message}>
            <select {...register('billing_tax_id_type')} className="select select-bordered w-full">
              <option value="rfc">RFC (México)</option>
              <option value="nit">NIT (Colombia)</option>
              <option value="rut">RUT (Chile)</option>
              <option value="ruc">RUC (Perú)</option>
            </select>
          </Field>

          <Field label="RFC / ID fiscal" error={errors.billing_tax_id_value?.message}>
            <input
              {...register('billing_tax_id_value')}
              className={twMerge('input input-bordered w-full', errors.billing_tax_id_value && 'input-error')}
              placeholder="XAXX010101000"
            />
          </Field>

          <Field label="Razón social" error={errors.billing_legal_name?.message}>
            <input
              {...register('billing_legal_name')}
              className={twMerge('input input-bordered w-full', errors.billing_legal_name && 'input-error')}
              placeholder="Empresa S.A. de C.V."
            />
          </Field>

          <Field label="Régimen fiscal" error={errors.billing_regime?.message}>
            <input
              {...register('billing_regime')}
              className={twMerge('input input-bordered w-full', errors.billing_regime && 'input-error')}
              placeholder="Persona moral"
            />
          </Field>
        </FormSection>

        {/* ── Bancarios ─────────────────────────────────────────────────── */}
        <FormSection title="Datos bancarios">
          <Field label="Tipo de cuenta" error={errors.bank_account_id_type?.message}>
            <select {...register('bank_account_id_type')} className="select select-bordered w-full">
              <option value="clabe">CLABE (México)</option>
              <option value="account_number">Número de cuenta</option>
              <option value="iban">IBAN</option>
            </select>
          </Field>

          <Field label="CLABE / Número de cuenta" error={errors.bank_account_id_value?.message}>
            <input
              {...register('bank_account_id_value')}
              className={twMerge('input input-bordered w-full', errors.bank_account_id_value && 'input-error')}
              placeholder="012345678901234567"
            />
          </Field>

          <Field label="Banco" error={errors.bank_name?.message}>
            <input
              {...register('bank_name')}
              className={twMerge('input input-bordered w-full', errors.bank_name && 'input-error')}
              placeholder="BBVA"
            />
          </Field>

          <Field label="Titular de la cuenta" error={errors.bank_holder_name?.message}>
            <input
              {...register('bank_holder_name')}
              className={twMerge('input input-bordered w-full', errors.bank_holder_name && 'input-error')}
              placeholder="Carlos Ramírez"
            />
          </Field>
        </FormSection>

        {/* ── Actions ───────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/dispatch-places" className="btn btn-ghost">
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={is_submitting}
            className="btn btn-success text-white"
          >
            {is_submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Crear punto de despacho
          </button>
        </div>
      </form>

      {/* ── Register Provider Modal ────────────────────────────────────── */}
      {is_register_modal_open && (
        <RegisterProviderModal
          onConfirm={onRegisterProvider}
          onClose={onCloseRegisterModal}
        />
      )}
    </div>
  );
}
